import React, { useState } from 'react';
import {
  CheckCircleIcon, XCircleIcon, BanknotesIcon, PaperAirplaneIcon, PencilSquareIcon, DocumentPlusIcon,
  LockClosedIcon, ArrowPathIcon, TrashIcon, InformationCircleIcon, ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import billingService from '../../services/billingService';
import { errText } from '../../services/scheduleService';
import { rands } from './TariffLines';

export const CLAIM_STATUS = {
  submitted: { label: 'Submitted to medical aid', short: 'Submitted', tone: 'bg-blue-100 text-blue-700' },
  sent_to_patient: { label: 'Sent to patient', short: 'Sent to patient', tone: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Accepted by medical aid', short: 'Accepted', tone: 'bg-teal-100 text-teal-800' },
  partially_paid: { label: 'Part paid', short: 'Part paid', tone: 'bg-amber-100 text-amber-800' },
  rejected: { label: 'Rejected', short: 'Rejected', tone: 'bg-red-100 text-red-700' },
  paid: { label: 'Paid', short: 'Paid', tone: 'bg-green-100 text-green-700' },
};
const DRAFT_STATUS = {
  preview: { label: 'Draft — not sent to billing', short: 'Draft', tone: 'bg-amber-100 text-amber-700' },
  awaiting_review: { label: 'Awaiting review', short: 'To review', tone: 'bg-indigo-100 text-indigo-700' },
  query: { label: 'Question with the doctor', short: 'With doctor', tone: 'bg-rose-100 text-rose-700' },
  discarded: { label: 'Discarded', short: 'Discarded', tone: 'bg-slate-200 text-slate-500' },
};
export const statusOf = (d) => (d.status === 'final' ? CLAIM_STATUS[d.claim_status] : DRAFT_STATUS[d.status]) || { label: d.status, short: d.status, tone: 'bg-slate-100 text-slate-600' };

const EVENT_ICON = {
  created: DocumentPlusIcon, edited: PencilSquareIcon, reviewed: CheckCircleIcon, discarded: TrashIcon,
  sent_for_review: PaperAirplaneIcon, query: ChatBubbleLeftRightIcon, query_answered: ChatBubbleLeftRightIcon,
  finalised: LockClosedIcon, submitted: PaperAirplaneIcon, sent_to_patient: PaperAirplaneIcon, resubmitted: ArrowPathIcon,
  accepted: CheckCircleIcon, rejected: XCircleIcon, payment: BanknotesIcon,
};
const EVENT_TONE = {
  accepted: 'text-teal-600', rejected: 'text-red-600', payment: 'text-green-600', finalised: 'text-brand-navy', submitted: 'text-blue-600',
  sent_to_patient: 'text-blue-600', sent_for_review: 'text-indigo-600', query: 'text-rose-600', query_answered: 'text-indigo-600',
};
const when = (d) => new Date(d).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/**
 * Timeline — every step an invoice has been through.
 */
export const Timeline = ({ events = [] }) => (
  <ol className="space-y-2">
    {events.map((e) => {
      const Icon = EVENT_ICON[e.event] || InformationCircleIcon;
      return (
        <li key={e.event_id} className="flex gap-2.5 text-xs">
          <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${EVENT_TONE[e.event] || 'text-slate-400'}`} />
          <div className="min-w-0 flex-1">
            <div className="text-slate-700">{e.note || e.event}</div>
            <div className="text-[11px] text-slate-400">{when(e.at)}{e.by_name ? ` · ${e.by_name}` : ''}</div>
          </div>
        </li>
      );
    })}
    {events.length === 0 && <li className="text-xs text-slate-400">No history yet.</li>}
  </ol>
);

/**
 * ClaimTracker — a final invoice's claim: where it is, what's been paid, and
 * the steps to record. SIMULATED until a claims switch is connected: the
 * practice records what the medical aid tells it.
 */
const ClaimTracker = ({ draft, onDraft }) => {
  const [mode, setMode] = useState(null); // 'reject' | 'payment'
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const status = draft.claim_status;
  const total = draft.total || 0;
  const paid = draft.amount_paid || 0;
  const outstanding = Math.round((total - paid) * 100) / 100;
  const can = {
    accept: status === 'submitted',
    reject: status === 'submitted',
    resubmit: status === 'rejected',
    payment: ['submitted', 'sent_to_patient', 'accepted', 'partially_paid'].includes(status),
  };

  const act = async (action, extra = {}) => {
    setBusy(action);
    setErr('');
    try {
      onDraft(await billingService.claim(draft.draft_id, { action, ...extra }));
      setMode(null);
      setNote('');
      setAmount('');
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy('');
    }
  };

  const s = statusOf(draft);
  const btn = 'rounded-lg border px-3 py-1.5 text-xs font-semibold disabled:opacity-50';

  return (
    <div className="border-t border-slate-200 bg-slate-50/60 px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-bold text-brand-deep">{draft.invoice_no}</span>
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${s.tone}`}>{s.label}</span>
        </div>
        <div className="flex flex-1 flex-wrap justify-end gap-4 text-xs">
          <span className="text-slate-500">Billed <b className="text-slate-800">{rands(total)}</b></span>
          <span className="text-slate-500">Paid <b className="text-green-700">{rands(paid)}</b></span>
          <span className="text-slate-500">Owing <b className={outstanding > 0 ? 'text-amber-700' : 'text-slate-800'}>{rands(outstanding)}</b></span>
        </div>
      </div>

      <p className="mt-2 flex items-start gap-1.5 text-[11px] text-slate-500">
        <InformationCircleIcon className="mt-px h-3.5 w-3.5 shrink-0" />
        No claims switch is connected yet, so submission is simulated. Record here what the medical aid or patient tells you.
      </p>

      {(can.accept || can.reject || can.resubmit || can.payment) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {can.accept && <button onClick={() => act('accept')} disabled={!!busy} className={`${btn} border-teal-300 bg-white text-teal-700 hover:bg-teal-50`}>{busy === 'accept' ? 'Saving…' : 'Mark accepted'}</button>}
          {can.reject && <button onClick={() => setMode(mode === 'reject' ? null : 'reject')} disabled={!!busy} className={`${btn} border-red-200 bg-white text-red-700 hover:bg-red-50`}>Mark rejected…</button>}
          {can.payment && (
            <button onClick={() => { setMode(mode === 'payment' ? null : 'payment'); setAmount(String(outstanding)); }} disabled={!!busy} className={`${btn} border-green-300 bg-white text-green-700 hover:bg-green-50`}>
              Record payment…
            </button>
          )}
          {can.resubmit && <button onClick={() => act('resubmit')} disabled={!!busy} className={`${btn} border-blue-200 bg-white text-blue-700 hover:bg-blue-50`}>{busy === 'resubmit' ? 'Saving…' : 'Resubmit'}</button>}
        </div>
      )}

      {mode === 'reject' && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason the medical aid gave" className="min-w-[14rem] flex-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm" />
          <button onClick={() => act('reject', { note })} disabled={!!busy || !note.trim()} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">{busy === 'reject' ? 'Saving…' : 'Save rejection'}</button>
        </div>
      )}
      {mode === 'payment' && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-500">R</span>
          <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-32 rounded-lg border border-slate-300 px-2.5 py-1.5 text-right text-sm tabular-nums" />
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reference (optional)" className="min-w-[10rem] flex-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm" />
          <button onClick={() => act('payment', { amount: Number(amount), note })} disabled={!!busy || !(Number(amount) > 0)} className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
            {busy === 'payment' ? 'Saving…' : 'Save payment'}
          </button>
        </div>
      )}
      {err && <p className="mt-2 text-sm text-red-600">{err}</p>}

      <h4 className="mb-1.5 mt-3 text-[11px] font-bold uppercase tracking-wide text-slate-500">History</h4>
      <Timeline events={draft.events} />
    </div>
  );
};

export default ClaimTracker;
